<template>
  <div
    v-if="blockingToast || nonBlockingToasts.length"
    :class="$style.region"
    :data-collapsing="isCollapsing ? 'true' : undefined"
    :data-dismissing="isDismissing ? 'true' : undefined"
    data-site-notification-toast-region
  >
    <Teleport to="body">
      <div
        v-if="showExpandedBackdrop"
        :class="$style.collectionBackdrop"
        :style="backdropStyle"
        aria-hidden="true"
        data-site-notification-expanded-backdrop
      />
    </Teleport>

    <div
      v-if="blockingToast"
      :class="$style.blockingRegion"
      data-site-notification-blocking
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <article
        :class="$style.toast"
        :data-site-notification-id="blockingToast.id"
      >
        <h2 :id="headingId(blockingToast)" :class="$style.heading">
          {{ blockingToast.heading }}
        </h2>
        <MarkedParser
          :id="bodyId(blockingToast)"
          :class="$style.body"
          :md="blockingToast.body"
        />
        <div v-if="blockingCtas.length" :class="$style.ctas">
          <CallToAction
            v-for="(cta, index) in blockingCtas"
            :key="ctaKey(cta, index)"
            :cta="cta"
            :parent-entry-title="blockingToast.entryTitle"
          />
        </div>
      </article>
    </div>

    <section
      v-if="nonBlockingToasts.length"
      ref="collectionElement"
      :class="$style.collection"
      data-site-notification-collection
      :data-collapsed="collapsed"
      :data-collapsing="isCollapsing ? 'true' : undefined"
      :data-stack-count="Math.min(nonBlockingToasts.length, 3)"
      :style="collectionStyle"
      role="region"
      aria-live="polite"
      :aria-label="collectionLabel"
    >
      <div
        :class="$style.collectionHeader"
        data-site-notification-collection-header
        :aria-hidden="collapsed ? 'true' : undefined"
      >
        <span data-site-notification-count :class="$style.collectionLabel">
          {{ collectionLabel }} ({{ nonBlockingToasts.length }})
        </span>
        <button
          type="button"
          :class="$style.collapseToggle"
          data-site-notification-collapse-toggle
          :aria-expanded="!collapsed"
          :aria-controls="collectionContentId"
          @click="requestToggleCollapse(!collapsed)"
        >
          {{ collapseLabel }}
        </button>
      </div>

      <div
        :id="collectionContentId"
        :class="$style.nonBlockingContent"
        data-site-notification-non-blocking-content
        @click="onCollapsedContentClick"
      >
        <TransitionGroup
          tag="div"
          name="site-notification-toast"
          :class="$style.toastList"
          data-site-notification-toast-list
          @before-leave="captureLeavePosition"
          @after-leave="clearLeavePosition"
        >
          <article
            v-for="toast in displayedNonBlockingToasts"
            :key="toast.id"
            :class="$style.toast"
            :style="toastStyle()"
            :data-site-notification-id="toast.id"
          >
            <header :class="$style.toastHeader">
              <h2 :id="headingId(toast)" :class="$style.heading">
                {{ toast.heading }}
              </h2>
              <button
                type="button"
                :class="$style.dismiss"
                :data-site-notification-dismiss="toast.id"
                :aria-label="dismissLabel(toast)"
                @click="emit('dismiss', toast.id)"
              >
                <span aria-hidden="true">×</span>
              </button>
            </header>
            <MarkedParser
              :id="bodyId(toast)"
              :class="$style.body"
              :md="toast.body"
            />
            <div v-if="ctaEntries(toast).length" :class="$style.ctas">
              <CallToAction
                v-for="(cta, index) in ctaEntries(toast)"
                :key="ctaKey(cta, index)"
                :cta="cta"
                :parent-entry-title="toast.entryTitle"
              />
            </div>
          </article>
        </TransitionGroup>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import type { TypeCallToActionWithoutUnresolvableLinksResponse } from '~/types/contentful'
import type { SiteNotification } from '~/types/siteNotification'

const props = withDefaults(
  defineProps<{
    blockingToast?: SiteNotification | null
    nonBlockingToasts?: SiteNotification[]
    collapsed?: boolean
  }>(),
  {
    blockingToast: null,
    nonBlockingToasts: () => [],
    collapsed: true,
  },
)

const TOAST_TRANSITION_MS = 300
const BACKDROP_SIDE_PADDING_PX = 32
const BACKDROP_TOP_PADDING_PX = 32
const BACKDROP_BOTTOM_PADDING_PX = 32

const emit = defineEmits<{
  dismiss: [entryId: string]
  'toggle-collapse': [collapsed: boolean]
}>()

const { localizedStrings } = useLocalizedStrings()
const collectionLabel = computed(
  () => localizedStrings.value['site-notifications'] || 'Site notifications',
)
const collapseLabel = computed(
  () => localizedStrings.value['collapse-site-notifications'] || 'Show less',
)
const collectionContentId = 'site-notification-toast-collection-content'
const blockingCtas = computed(() => ctaEntries(props.blockingToast))
const isCollapsing = ref(false)
let collapseResetTimer: ReturnType<typeof setTimeout> | null = null
const isDismissing = ref(false)
let dismissResetTimer: ReturnType<typeof setTimeout> | null = null
const collectionElement = ref<HTMLElement | null>(null)
const backdropRect = ref({ top: 0, left: 0, width: 0, height: 0 })
let backdropResizeObserver: ResizeObserver | null = null

const updateBackdropRect = () => {
  const element = collectionElement.value
  if (!element || props.collapsed) {
    if (backdropRect.value.width || backdropRect.value.height) {
      backdropRect.value = { top: 0, left: 0, width: 0, height: 0 }
    }
    return
  }

  const { top, left, width, height } = element.getBoundingClientRect()
  if (
    top === backdropRect.value.top &&
    left === backdropRect.value.left &&
    width === backdropRect.value.width &&
    height === backdropRect.value.height
  ) {
    return
  }

  backdropRect.value = { top, left, width, height }
}
const observeBackdrop = () => {
  backdropResizeObserver?.disconnect()
  backdropResizeObserver = null

  if (!collectionElement.value || typeof ResizeObserver === 'undefined') return

  backdropResizeObserver = new ResizeObserver(updateBackdropRect)
  backdropResizeObserver.observe(collectionElement.value)
}
const showExpandedBackdrop = computed(
  () => !props.collapsed && backdropRect.value.width > 0,
)
const backdropStyle = computed(() => ({
  top: `${backdropRect.value.top - BACKDROP_TOP_PADDING_PX}px`,
  left: `${backdropRect.value.left - BACKDROP_SIDE_PADDING_PX}px`,
  width: `${backdropRect.value.width + BACKDROP_SIDE_PADDING_PX * 2}px`,
  height: `${backdropRect.value.height + BACKDROP_TOP_PADDING_PX + BACKDROP_BOTTOM_PADDING_PX}px`,
  '--site-notification-backdrop-top-padding': `${BACKDROP_TOP_PADDING_PX}px`,
  '--site-notification-backdrop-side-padding': `${BACKDROP_SIDE_PADDING_PX}px`,
  '--site-notification-backdrop-bottom-padding': `${BACKDROP_BOTTOM_PADDING_PX}px`,
}))
const displayedNonBlockingToasts = computed(() =>
  props.collapsed
    ? props.nonBlockingToasts.slice(0, 1)
    : props.nonBlockingToasts,
)
const collectionStyle = computed(() => ({
  '--site-notification-collapse-delay': '0ms',
}))
const toastStyle = () => ({
  '--site-notification-toast-collapse-delay': '0ms',
})

const idPart = (entry: SiteNotification) =>
  entry.id.replace(/[^a-zA-Z0-9_-]/g, '-')
const headingId = (entry: SiteNotification) =>
  `site-notification-${idPart(entry)}-heading`
const bodyId = (entry: SiteNotification) =>
  `site-notification-${idPart(entry)}-body`
const ctaEntries = (entry: SiteNotification | null | undefined) =>
  (entry?.ctas ||
    []) as unknown as TypeCallToActionWithoutUnresolvableLinksResponse[]
const ctaKey = (
  cta: TypeCallToActionWithoutUnresolvableLinksResponse,
  index: number,
) =>
  cta.sys?.id ||
  (cta as unknown as { id?: string }).id ||
  `site-notification-cta-${index}`
const dismissLabel = (entry: SiteNotification) =>
  entry.dismissLabel.trim() ||
  localizedStrings.value['close-modal'] ||
  'Close Modal'

const clearCollapseResetTimer = () => {
  if (collapseResetTimer === null) return
  clearTimeout(collapseResetTimer)
  collapseResetTimer = null
}

const clearDismissResetTimer = () => {
  if (dismissResetTimer === null) return
  clearTimeout(dismissResetTimer)
  dismissResetTimer = null
}

const markDismissing = () => {
  clearDismissResetTimer()
  isDismissing.value = true
  dismissResetTimer = setTimeout(() => {
    isDismissing.value = false
    dismissResetTimer = null
  }, TOAST_TRANSITION_MS + 20)
}

const leaveOffsets = new WeakMap<HTMLElement, number>()

const requestToggleCollapse = (nextCollapsed: boolean) => {
  clearCollapseResetTimer()
  isCollapsing.value = nextCollapsed

  if (nextCollapsed) {
    collapseResetTimer = setTimeout(() => {
      isCollapsing.value = false
      collapseResetTimer = null
    }, TOAST_TRANSITION_MS + 20)
  }

  emit('toggle-collapse', nextCollapsed)
}

const captureLeavePosition = (element: Element) => {
  const toast = element as HTMLElement
  const list = toast.parentElement
  if (!list) return

  if (!leaveOffsets.has(toast)) {
    const listRect = list.getBoundingClientRect()
    for (const child of Array.from(list.children)) {
      const childElement = child as HTMLElement
      const childRect = childElement.getBoundingClientRect()
      leaveOffsets.set(childElement, childRect.top - listRect.top)
    }
  }

  const top = leaveOffsets.get(toast)
  if (top === undefined) return
  toast.style.setProperty('--site-notification-toast-leave-top', `${top}px`)
}

const clearLeavePosition = (element: Element) => {
  leaveOffsets.delete(element as HTMLElement)
}

const onCollapsedContentClick = (event: MouseEvent) => {
  if (!props.collapsed) return

  const target = event.target
  if (
    target instanceof Element &&
    target.closest('button, a, input, select, textarea')
  ) {
    return
  }

  requestToggleCollapse(false)
}

watch(
  () => [props.collapsed, props.nonBlockingToasts.length],
  async () => {
    await nextTick()
    observeBackdrop()
    updateBackdropRect()
  },
)

watch(
  () => props.nonBlockingToasts.length,
  (length, previousLength) => {
    if (length < previousLength) markDismissing()
  },
)

onMounted(() => {
  observeBackdrop()
  updateBackdropRect()
  window.addEventListener('resize', updateBackdropRect)
})

onBeforeUnmount(() => {
  clearCollapseResetTimer()
  clearDismissResetTimer()
  backdropResizeObserver?.disconnect()
  window.removeEventListener('resize', updateBackdropRect)
})
</script>

<style lang="scss" module>
.region {
  position: fixed;
  top: 60px;
  right: max(
    var(--site-header-content-padding-x, 15px),
    calc(
      (100vw - var(--site-header-content-max-width, 1600px)) / 2 +
        var(--site-header-content-padding-x, 15px)
    )
  );
  z-index: 9;
  display: grid;
  gap: 10px;
  width: min(400px, calc(100vw - 24px));
  max-width: 100%;
  max-height: calc(100vh - 72px);
  overflow: visible;
  padding: 12px 0;
}

.region[data-collapsing='true'],
.region[data-dismissing='true'] {
  overflow: visible;
}

.blockingRegion,
.collection {
  display: grid;
  gap: 8px;
}

.collectionBackdrop {
  position: fixed;
  z-index: 8;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  mask-image:
    linear-gradient(
      to right,
      transparent,
      #000 var(--site-notification-backdrop-side-padding),
      #000 calc(100% - var(--site-notification-backdrop-side-padding)),
      transparent
    ),
    linear-gradient(
      to bottom,
      transparent,
      #000 var(--site-notification-backdrop-top-padding),
      #000 calc(100% - var(--site-notification-backdrop-bottom-padding)),
      transparent
    );
  mask-composite: intersect;
  -webkit-mask-image:
    linear-gradient(
      to right,
      transparent,
      #000 var(--site-notification-backdrop-side-padding),
      #000 calc(100% - var(--site-notification-backdrop-side-padding)),
      transparent
    ),
    linear-gradient(
      to bottom,
      transparent,
      #000 var(--site-notification-backdrop-top-padding),
      #000 calc(100% - var(--site-notification-backdrop-bottom-padding)),
      transparent
    );
  -webkit-mask-composite: source-in;
}

.toast {
  position: relative;
  padding: 14px 16px;
  color: var(--black);
  background: var(--white);
  border: 1px solid #e5e7eb;
}

.toast::before {
  position: absolute;
  inset-block: -1px;
  inset-inline-start: -1px;
  width: 4px;
  content: '';
  background: var(--purple);
  pointer-events: none;
}

.toastHeader,
.collectionHeader {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  justify-content: space-between;
}

.heading {
  font-family: var(--roboto-condensed);
  font-size: 22px;
  line-height: 1.15;
}

.body {
  margin-top: 6px;
  font-size: 14px;
  line-height: 1.4;
  text-wrap: pretty;

  :global(p),
  :global(ul),
  :global(ol) {
    margin-bottom: 8px;
  }
}

.dismiss,
.collapseToggle {
  flex: 0 0 auto;
  color: var(--purple);
  background: transparent;
  border: 0;
}

.dismiss {
  width: 28px;
  height: 28px;
  padding: 0;
  font-size: 22px;
  line-height: 1;
}

.collection {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: calc(100vh - 72px);
  box-sizing: border-box;
  padding: 10px 0;
}

.collectionHeader {
  flex: 0 0 auto;
  position: relative;
  z-index: 1;
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
  color: var(--white);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
  transition:
    opacity 300ms ease,
    visibility 0s linear 300ms;
}

.collection[data-collapsed='false'] .collectionHeader {
  opacity: 1;
  pointer-events: auto;
  visibility: visible;
  transition-delay: 0s;
}

.collection[data-collapsing='true'] .collectionHeader,
.collection[data-collapsing='true'] .nonBlockingContent {
  transition-delay: var(--site-notification-collapse-delay);
}

.nonBlockingContent {
  position: relative;
  z-index: 1;
  min-height: 0;
  flex: 1 1 auto;
  display: grid;
  gap: 8px;
  transition: transform 300ms ease;
}

.collection[data-collapsed='false'] .nonBlockingContent {
  overflow-y: auto;
  overscroll-behavior: contain;
}

.toastList {
  position: relative;
  display: grid;
  gap: 8px;
}

:global(.site-notification-toast-move),
:global(.site-notification-toast-enter-active),
:global(.site-notification-toast-leave-active) {
  transition:
    opacity 300ms ease,
    transform 300ms ease;
}

:global(.site-notification-toast-leave-active) {
  position: absolute;
  inset-inline: 0;
  top: var(--site-notification-toast-leave-top, 0px);
}

:global(.site-notification-toast-enter-from) {
  opacity: 0;
  transform: translateY(-25px);
}

:global(.site-notification-toast-leave-to) {
  opacity: 0;
  transform: translateY(-20px);
}

.collection[data-collapsing='true']
  :global(.site-notification-toast-leave-active) {
  transition-delay: var(--site-notification-toast-collapse-delay, 0ms);
}

:global(.site-notification-toast-leave-active) {
  animation: none !important;
}

:global(.site-notification-toast-move) {
  animation: none !important;
}

.collection[data-collapsed='true'] .nonBlockingContent {
  padding-bottom: 14px;
  transform: translateY(-30px);
}

.nonBlockingContent::before,
.nonBlockingContent::after {
  position: absolute;
  inset: 0 6px 14px;
  content: '';
  pointer-events: none;
  background: var(--white);
  border: 1px solid #e5e7eb;
  opacity: 0;
  transform: translateY(-6px);
  transition:
    opacity 300ms ease,
    transform 300ms ease;
}

.collection[data-collapsed='true'] .nonBlockingContent::before {
  right: 12px;
  left: 12px;
  z-index: 0;
  display: block;
  opacity: 1;
  transform: translateY(6px);
}

.collection[data-collapsed='true'] .nonBlockingContent::after {
  right: 24px;
  left: 24px;
  z-index: -1;
  display: block;
  opacity: 1;
  transform: translateY(12px);
}

.collection[data-collapsed='true']:not([data-collapsing='true'])
  .nonBlockingContent:hover::before {
  transform: translateY(10px);
}

.collection[data-collapsed='true']:not([data-collapsing='true'])
  .nonBlockingContent:hover::after {
  transform: translateY(20px);
}

.collection[data-collapsed='false'] .nonBlockingContent::before,
.collection[data-collapsed='false'] .nonBlockingContent::after {
  display: none;
}

.collection[data-collapsing='true'][data-collapsed='true']
  .nonBlockingContent::before,
.collection[data-collapsing='true'][data-collapsed='true']
  .nonBlockingContent::after {
  opacity: 0;
  transform: translateY(-6px);
  transition-delay: 0s;
}

.collection[data-collapsed='true'][data-stack-count='1']
  .nonBlockingContent::before,
.collection[data-collapsed='true'][data-stack-count='1']
  .nonBlockingContent::after,
.collection[data-collapsed='true'][data-stack-count='2']
  .nonBlockingContent::after {
  display: none;
}

.collection[data-collapsed='true'] .toast {
  z-index: 1;
}

.collection[data-collapsed='true']:not([data-collapsing='true'])
  .toastList
  > .toast:first-child {
  cursor: pointer;
}

.collectionLabel {
  font-weight: bold;
}

.collapseToggle {
  min-width: 0;
  padding: 0;
  color: var(--white);
  font: inherit;
  font-weight: bold;
  text-decoration: none;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
  white-space: nowrap;
  background: transparent;
  border: 0;
}

.collapseToggle:focus-visible {
  outline: 3px solid var(--white);
  outline-offset: 2px;
}

.ctas {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

@media (max-width: 640px) {
  .region {
    right: 0;
    width: 100%;
    max-width: 100%;
    padding: 8px 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .nonBlockingContent {
    transition: none;
  }

  .nonBlockingContent::before,
  .nonBlockingContent::after {
    transition: none;
  }

  :global(.site-notification-toast-move),
  :global(.site-notification-toast-enter-active),
  :global(.site-notification-toast-leave-active) {
    transition: none;
  }

  .collection[data-collapsed='false'] .collectionHeader {
    transition: none;
  }
}
</style>
